import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CourseLessonDocumentLinkReferenceEntity, CourseLessonEntity } from '../../../../meta-data/app-meta-data.service';
import { LessonProgressHierarchy } from '../../../courses/models/lesson-progress-hierarchy.model';
import { QuizProgressEvent } from '../../../quizzes/models/quiz-progress-event.model';
import { DocumentProgressEvent } from '../../models/document-progress-event.model';
import { HelpScoutBeaconService } from '../../../help-scout-beacon/services/help-scout-beacon.service';
import { environment } from '../../../../../environments/environment';
import { StructuredDocumentSectionReferenceModel } from '../../structured-content/models/structured-document-viewer.model';

@Component({
  selector: 'app-document-lesson',
  templateUrl: './document-lesson.component.html',
  styleUrls: ['./document-lesson.component.scss']
})
export class DocumentLessonComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() lesson: CourseLessonEntity;
  @Input() progress: LessonProgressHierarchy;
  @Output() documentEvent = new EventEmitter<DocumentProgressEvent>();
  @Output() widthChanged = new EventEmitter<string>();

  structuredDocumentReferences: StructuredDocumentSectionReferenceModel[] = [];

  aspectRatio: number = 16 / 9;

  width: number;
  height: number;
  maxHeight: number;
  finalHeight: number;
  finalWidth: string;
  previousWidth: number;
  mobileWidth = 850;
  viewerStyle: any = {
    width: '100%',
    'min-width': '350px',
    'min-height': '196px',
    'max-width': '1200px'
  };

  constructor(private hostElementRef: ElementRef, private helpScoutBeaconService: HelpScoutBeaconService) { }

  startTime = performance.now();

  markAsCompletedAfterMs = 10 * 1000;

  eventEmitFrequency = 10 * 1000;
  maxEventTime = 120 * 1000;

  interval: any;

  // Kept so we can disconnect on destroy. Re-measures the viewer whenever the
  // host's box actually changes (sidebar toggle, window chrome, etc.). Typed
  // loosely because TS 4.0's lib.dom doesn't ship ResizeObserver typings.
  private resizeObserver?: { disconnect(): void };

  ngOnInit(): void {
    // NOTE: don't measure here. On an in-app lesson→lesson navigation the host
    // hasn't been laid out yet when ngOnInit fires, so getBoundingClientRect()
    // returns a stale `top` and setupStyle() clamps the viewer to a smaller box
    // (the bug where the ProTool card shrinks until you refresh). Measurement is
    // deferred to ngAfterViewInit, after the browser has laid the new view out.
    this.turnOffBeacon();

    this.interval =
      setInterval(() => {
        this.onDocumentEvent();

      }, this.eventEmitFrequency);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lesson) {
      this.structuredDocumentReferences = this.buildStructuredDocumentReferences();
    }
  }

  ngAfterViewInit(): void {
    // First measurement, after layout has settled. A single rAF fires before
    // the post-navigation layout is final in some cases, so we wait two frames
    // to guarantee getBoundingClientRect().top (which feeds maxHeight) is real.
    this.scheduleMeasure();

    // Keep the viewer correctly sized after the initial paint: a ResizeObserver
    // on the host re-runs setupStyle() whenever its box genuinely changes. The
    // `previousWidth === width` guard inside setupStyle() prevents this from
    // looping when only the height we set changes.
    const ResizeObserverCtor = (window as any).ResizeObserver;
    if (ResizeObserverCtor && this.hostElementRef?.nativeElement) {
      const observer = new ResizeObserverCtor(() => this.setupStyle());
      observer.observe(this.hostElementRef.nativeElement);
      this.resizeObserver = observer;
    }
  }

  private scheduleMeasure(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      // Force a recompute even if the early (wrong) width happened to match —
      // offsetTop/maxHeight may have changed without the width changing.
      this.previousWidth = undefined;
      this.setupStyle();
    }));
  }

  ngOnDestroy(): void {
    this.onDocumentEvent();

    this.clearInterval();

    this.resizeObserver?.disconnect();

    this.turnOnBeacon();
  }


  turnOnBeacon() {
    this.helpScoutBeaconService.turnOnBeacon();
  }

  turnOffBeacon() {
    if (environment.turnOffBeaconForVideos) {
      this.helpScoutBeaconService.turnOffBeacon();
    }
  }

  onDocumentEvent() {
    const endTime = performance.now();

    const executionTime = endTime - this.startTime;

    if (executionTime > this.maxEventTime) {
      // We've reached the max time, stop sending events after this one so the max time will be logged
      this.clearInterval();
    }

    const event: DocumentProgressEvent = {
      secondsViewed: executionTime,
      isComplete: executionTime >= this.markAsCompletedAfterMs
    };

    this.documentEvent.emit(event);
  }

  private clearInterval() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  @HostListener('window:resize', ['$event'])
  public resize(event: any): void {
    // re-configure the grid style upon browser resize
    this.setupStyle();
  }

  setupStyle() {

    // Calculate the viewer style based on the current width of the host element and using the aspect ratio
    if (!this.hostElementRef?.nativeElement) {
      return;
    }
    this.width = this.hostElementRef.nativeElement?.firstChild?.clientWidth;
    const offsetTop: number = this.hostElementRef.nativeElement?.getBoundingClientRect()?.top || 0;

    if (!this.width) {

      setTimeout(() => {
        this.setupStyle();

      }, 50);
      return;
    }

    if (this.previousWidth === this.width) {
      return;
    }

    this.previousWidth = this.width;

    this.height = Math.round(this.width / this.aspectRatio) + 2;

    this.maxHeight = window.innerHeight - offsetTop - 100;

    this.finalHeight = this.height;
    this.finalWidth = '100%';

    if (this.finalHeight > this.maxHeight && this.width > this.mobileWidth) {
      this.finalHeight = this.maxHeight;
      const adjustedWidth = Math.round(this.finalHeight * this.aspectRatio) - 2;

      this.finalWidth = `${adjustedWidth}px`;
    }

    this.viewerStyle = {
      'width': this.finalWidth,
      'min-width': '350px',
      'min-height': '196px',
      height: `${this.finalHeight}px`
    };

    // Set CSS variables with the dynamic width and height for embedded content
    const rootStyle: any = document.querySelector(':root');

    rootStyle.style?.setProperty('--aviator-embed-width', this.finalWidth);
    rootStyle.style?.setProperty('--aviator-embed-height', `${this.finalHeight}px`);
    
    this.widthChanged.emit(this.finalWidth);
  }

  private buildStructuredDocumentReferences(): StructuredDocumentSectionReferenceModel[] {
    const documentItemId = this.lesson?.DocumentItem?.DocumentItemId || this.lesson?.DocumentItemId;
    if (!documentItemId || !this.lesson?.CourseLessonDocumentLink?.length) {
      return [];
    }

    const references: StructuredDocumentSectionReferenceModel[] = [];
    const seenSectionIds = new Set<number>();

    this.lesson.CourseLessonDocumentLink
      .filter(link => link.DocumentItemId === documentItemId)
      .forEach(link => {
        (link.CourseLessonDocumentLinkReference || [])
          .forEach(reference => {
            const documentSectionId = reference.DocumentSectionId;
            if (!documentSectionId || seenSectionIds.has(documentSectionId)) {
              return;
            }

            seenSectionIds.add(documentSectionId);
            references.push(this.mapStructuredDocumentReference(reference));
          });
      });

    return references.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }

  private mapStructuredDocumentReference(reference: CourseLessonDocumentLinkReferenceEntity): StructuredDocumentSectionReferenceModel {
    const section = reference.DocumentSection;
    const documentSectionId = reference.DocumentSectionId || 0;
    const label = reference.ReferenceLocatorText || section?.Label || section?.SectionKey || reference.DocumentSectionIdDisplay || '';

    return {
      documentItemId: this.lesson?.DocumentItem?.DocumentItemId || this.lesson?.DocumentItemId,
      documentVersionId: reference.DocumentVersionId,
      documentSectionId,
      label,
      title: section?.Title,
      displayOrder: reference.DisplayOrder
    };
  }

}
